import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

function UpdateRole() {
  const { roleId } = useParams();
  const authUser = useSelector((state) => state.auth.user);
  const CompID = authUser?.userData?.compID;
  const loggedInUserRoles = authUser?.userData?.userRoles;
  const canViewPermanentRoles =
    loggedInUserRoles?.isAdmin === true || loggedInUserRoles?.isAdmin === 1;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [isPermanentRole, setIsPermanentRole] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  
  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  


  const [formData, setFormData] = useState({
    ComRolePermission_ID: 0,
    R_Title: "",
    IsAdmin: false,
    ISBackOffice: false,
    ISStaffAPP: false,
    AllowAdminAccessOntheTill: false,
    AllowTillSettings: false,
    AllowPettyCashes: false,
    AllowFloatAdjustments: false,
    AllowBackOfficeAccessFromTill: false,
    AllowAccessToCustomerList: false,
    AllowTillClosingBlindEndofDay: false,
    AllowAllVoidLines: false,
    AllowTablePlanning: false,
    CanManageSchedule: false,
    CanAccessPOSwithoutClockin: false,
    CanAccessReports: false,
    CanBypassLogin: false,
    AllowBasketDiscounts: false,
    BasketDiscountLimit: null,
    BasketDiscountLimitPercentage: null,
    AllowRefunds: false,
    RefundLimit: null,
    IsPermanentRole: false,
    CompID: CompID || 0,
  });
  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      R_Title: formData.R_Title || "",
      IsAdmin: Boolean(formData.IsAdmin),
      ISBackOffice: Boolean(formData.ISBackOffice),
      ISStaffAPP: Boolean(formData.ISStaffAPP),
      AllowAdminAccessOntheTill: Boolean(formData.AllowAdminAccessOntheTill),
      AllowTillSettings: Boolean(formData.AllowTillSettings),
      AllowPettyCashes: Boolean(formData.AllowPettyCashes),
      AllowFloatAdjustments: Boolean(formData.AllowFloatAdjustments),
      AllowBackOfficeAccessFromTill: Boolean(formData.AllowBackOfficeAccessFromTill),
      AllowAccessToCustomerList: Boolean(formData.AllowAccessToCustomerList),
      AllowTillClosingBlindEndofDay: Boolean(formData.AllowTillClosingBlindEndofDay),
      AllowAllVoidLines: Boolean(formData.AllowAllVoidLines),
      AllowTablePlanning: Boolean(formData.AllowTablePlanning),
      CanManageSchedule: Boolean(formData.CanManageSchedule),
      CanAccessPOSwithoutClockin: Boolean(formData.CanAccessPOSwithoutClockin),
      CanAccessReports: Boolean(formData.CanAccessReports),
      CanBypassLogin: Boolean(formData.CanBypassLogin),
      AllowBasketDiscounts: Boolean(formData.AllowBasketDiscounts),
      BasketDiscountLimit: formData.BasketDiscountLimit ?? null,
      BasketDiscountLimitPercentage: formData.BasketDiscountLimitPercentage ?? null,
      AllowRefunds: Boolean(formData.AllowRefunds),
      RefundLimit: formData.RefundLimit ?? null,
    });
  }, [formData]);

  const isDirty =
    !isViewOnly &&
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  useEffect(() => {
    const fetchRoleData = async () => {
      setFetching(true);
      try {
        const response = await axiosInstance.get(APIs.GET_ROLE_BY_ID, {
          params: { CompID, id: roleId },
        });
     
        if (response.data.status && response.data.data) {
          const roleData = response.data.data;
          
          if (roleData.isPermanentRole === true || roleData.isPermanentRole === 1) {
            setIsPermanentRole(true);
            if (!canViewPermanentRoles) {
              showErrorToast("This is a permanent role and cannot be edited.");
              navigate("/role/manageRole");
              return;
            }
          }
         
          const loaded = {
            ComRolePermission_ID: roleData.comRolePermission_ID,
            R_Title: roleData.r_Title,
            IsAdmin: roleData.isAdmin,
            ISBackOffice: roleData.isBackOffice,
            ISStaffAPP: roleData.isStaffAPP ?? false,
            AllowAdminAccessOntheTill: roleData.allowAdminAccessOntheTill,
            AllowTillSettings: roleData.allowTillSettings,
            AllowPettyCashes: roleData.allowPettyCashes,
            AllowFloatAdjustments: roleData.allowFloatAdjustments,
            AllowBackOfficeAccessFromTill: roleData.allowBackOfficeAccessFromTill,
            AllowAccessToCustomerList: roleData.allowAccessToCustomerList,
            AllowTillClosingBlindEndofDay: roleData.allowTillClosingBlindEndofDay,
            AllowAllVoidLines: roleData.allowAllVoidLines,
            AllowTablePlanning: roleData.allowTablePlanning,
            CanManageSchedule: roleData.canManageSchedule ?? false,
            CanAccessPOSwithoutClockin: roleData.canAccessPOSwithoutClockin ?? false,
            CanAccessReports: roleData.canAccessReports ?? false,
            CanBypassLogin: roleData.canBypassLogin ?? false,
            AllowBasketDiscounts: roleData.allowBasketDiscounts,
            BasketDiscountLimit: roleData.basketDiscountLimit ?? null,
            BasketDiscountLimitPercentage: roleData.basketDiscountLimitPercentage ?? null,
            AllowRefunds: roleData.allowRefunds,
            RefundLimit: roleData.refundLimit ?? null,
            IsPermanentRole: roleData.isPermanentRole ?? false,
            CompID: CompID || roleData.compID,
          };
          setFormData(loaded);
          initialFormSnapshotRef.current = JSON.stringify({
            R_Title: loaded.R_Title || "",
            IsAdmin: Boolean(loaded.IsAdmin),
            ISBackOffice: Boolean(loaded.ISBackOffice),
            ISStaffAPP: Boolean(loaded.ISStaffAPP),
            AllowAdminAccessOntheTill: Boolean(loaded.AllowAdminAccessOntheTill),
            AllowTillSettings: Boolean(loaded.AllowTillSettings),
            AllowPettyCashes: Boolean(loaded.AllowPettyCashes),
            AllowFloatAdjustments: Boolean(loaded.AllowFloatAdjustments),
            AllowBackOfficeAccessFromTill: Boolean(loaded.AllowBackOfficeAccessFromTill),
            AllowAccessToCustomerList: Boolean(loaded.AllowAccessToCustomerList),
            AllowTillClosingBlindEndofDay: Boolean(loaded.AllowTillClosingBlindEndofDay),
            AllowAllVoidLines: Boolean(loaded.AllowAllVoidLines),
            AllowTablePlanning: Boolean(loaded.AllowTablePlanning),
            CanManageSchedule: Boolean(loaded.CanManageSchedule),
            CanAccessPOSwithoutClockin: Boolean(loaded.CanAccessPOSwithoutClockin),
            CanAccessReports: Boolean(loaded.CanAccessReports),
            CanBypassLogin: Boolean(loaded.CanBypassLogin),
            AllowBasketDiscounts: Boolean(loaded.AllowBasketDiscounts),
            BasketDiscountLimit: loaded.BasketDiscountLimit ?? null,
            BasketDiscountLimitPercentage: loaded.BasketDiscountLimitPercentage ?? null,
            AllowRefunds: Boolean(loaded.AllowRefunds),
            RefundLimit: loaded.RefundLimit ?? null,
          });
        } else {
          showErrorToast("Role not found");
          navigate("/role/manageRole");
        }
      } catch (error) {
  
        showErrorToast("Failed to fetch role data");
        navigate("/role/manageRole");
      } finally {
        setFetching(false);
      }
    };

  
    if (roleId && CompID) {
      fetchRoleData();
    }
  }, [roleId, CompID, navigate, canViewPermanentRoles]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleToggleChange = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (isViewOnly) {
      showErrorToast("This role cannot be updated.");
      return false;
    }

    if (!formData.R_Title.trim()) {
      showErrorToast("Role title is required");
      return false;
    }

    setLoading(true);

    try {
      const rolePayload = {
        ComRolePermission_ID: parseInt(roleId),
        CompID,
        R_Title: formData.R_Title,
        IsAdmin: formData.IsAdmin,
        ISBackOffice: formData.ISBackOffice,
        ISStaffAPP: formData.ISStaffAPP,
        AllowAdminAccessOntheTill: formData.AllowAdminAccessOntheTill,
        AllowTillSettings: formData.AllowTillSettings,
        AllowPettyCashes: formData.AllowPettyCashes,
        AllowFloatAdjustments: formData.AllowFloatAdjustments,
        AllowBackOfficeAccessFromTill: formData.AllowBackOfficeAccessFromTill,
        AllowAccessToCustomerList: formData.AllowAccessToCustomerList,
        AllowTillClosingBlindEndofDay: formData.AllowTillClosingBlindEndofDay,
        AllowAllVoidLines: formData.AllowAllVoidLines,
        AllowTablePlanning: formData.AllowTablePlanning,
        CanManageSchedule: formData.CanManageSchedule,
        CanAccessPOSwithoutClockin: formData.CanAccessPOSwithoutClockin,
        CanAccessReports: formData.CanAccessReports,
        CanBypassLogin: formData.CanBypassLogin,
        AllowBasketDiscounts: formData.AllowBasketDiscounts,
        BasketDiscountLimit: formData.BasketDiscountLimit ? parseFloat(formData.BasketDiscountLimit) : null,
        BasketDiscountLimitPercentage: formData.BasketDiscountLimitPercentage ? parseFloat(formData.BasketDiscountLimitPercentage) : null,
        AllowRefunds: formData.AllowRefunds,
        RefundLimit: formData.RefundLimit ? parseFloat(formData.RefundLimit) : null,
        // Permanent roles keep their flag; title stays locked in the UI
        IsPermanentRole: isPermanentRole ? true : formData.IsPermanentRole,
      };

      const response = await axiosInstance.put(
        APIs.UPDATE_ROLE,
        rolePayload
      );
      if (!response.data.status) {
        showErrorToast(response.data.message || "Failed to update role");
        return false;
      }

      showSuccessToast("Role updated successfully");
      initialFormSnapshotRef.current = getFormSnapshot();
      if (exitAfterSave) {
        navigate("/role/manageRole");
      }
      return true;
    } catch (error) {

      showErrorToast(
        error.response?.data?.message ||
          "An error occurred while updating the role"
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
    enabled: !isViewOnly,
  });

  if (fetching) {
    return (<GridSectionLoader loading={true} minHeight="70vh" />);
  }

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/role/manageRole">Manage Roles</Link>
            </li>
            <li className="breadcrumb-item active">{isViewOnly ? "View Role" : "Update Role"}</li>
          </ol>
          <h1 className="page-header mb-0">{isViewOnly ? "View Role" : "Update Role"}</h1>
        </div>
        {!isViewOnly && (
          <SaveActions
            onCancel={() => navigate("/role/manageRole")}
            onSave={() => handleSubmit({ exitAfterSave: false })}
            onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
            saving={loading || fetching}
          />
        )}
      </div>
      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <div className="row mb-3">
          {/* Title Panel */}
          <div className="col-xl-12">
            <Panel className="m-3" data-sortable-id="form-stuff-1">
              <PanelHeader>
                <div className="panel-title">Title</div>
              </PanelHeader>
              <PanelBody>
                <div className="col-12">
                  <FormInput
                    label="Title"
                    placeHolder="Enter Role Title"
                    type="text"
                    value={formData.R_Title}
                    onchange={handleInputChange}
                    name="R_Title"
                    readOnly={isViewOnly || isPermanentRole}
                    disabled={isViewOnly || isPermanentRole}
                  />
                  {isPermanentRole && !isViewOnly && (
                    <small className="text-muted">
                      Permanent role name cannot be changed. Permissions can still be updated.
                    </small>
                  )}
                </div>
              </PanelBody>
            </Panel>
          </div>

          {/* Admin Access Panel */}
          <div className="col-xl-12">
            <Panel className="m-3" data-sortable-id="form-stuff-1">
              <PanelHeader>
                <h4 className="panel-title">Admin Access</h4>
              </PanelHeader>
              <PanelBody>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.IsAdmin}
                        onToggle={(checked) => handleToggleChange(checked, "IsAdmin")}
                        label="Is Admin"
                        name="IsAdmin"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.ISBackOffice}
                        onToggle={(checked) => handleToggleChange(checked, "ISBackOffice")}
                        label="Back Office"
                        name="ISBackOffice"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.ISStaffAPP}
                        onToggle={(checked) => handleToggleChange(checked, "ISStaffAPP")}
                        label="Staff App"
                        name="ISStaffAPP"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowAdminAccessOntheTill}
                        onToggle={(checked) => handleToggleChange(checked, "AllowAdminAccessOntheTill")}
                        label="Admin Access On Till"
                        name="AllowAdminAccessOntheTill"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowTillSettings}
                        onToggle={(checked) => handleToggleChange(checked, "AllowTillSettings")}
                        label="Allow Till Settings"
                        name="AllowTillSettings"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowPettyCashes}
                        onToggle={(checked) => handleToggleChange(checked, "AllowPettyCashes")}
                        label="Allow Petty Cashes"
                        name="AllowPettyCashes"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowFloatAdjustments}
                        onToggle={(checked) => handleToggleChange(checked, "AllowFloatAdjustments")}
                        label="Allow Float Adjustments"
                        name="AllowFloatAdjustments"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowBackOfficeAccessFromTill}
                        onToggle={(checked) => handleToggleChange(checked, "AllowBackOfficeAccessFromTill")}
                        label="Allow BackOffice Access From Till"
                        name="AllowBackOfficeAccessFromTill"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowAccessToCustomerList}
                        onToggle={(checked) => handleToggleChange(checked, "AllowAccessToCustomerList")}
                        label="Allow Access To Customer List"
                        name="AllowAccessToCustomerList"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowTillClosingBlindEndofDay}
                        onToggle={(checked) => handleToggleChange(checked, "AllowTillClosingBlindEndofDay")}
                        label="Allow Till Closing Blind End of Day"
                        name="AllowTillClosingBlindEndofDay"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                </div>
              </PanelBody>
            </Panel>
          </div>

          {/* Transaction Options Panel */}
          <div className="col-xl-12">
            <Panel className="m-3" data-sortable-id="form-stuff-1">
              <PanelHeader>
                <h4 className="panel-title">Transaction Options</h4>
              </PanelHeader>
              <PanelBody>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowAllVoidLines}
                        onToggle={(checked) => handleToggleChange(checked, "AllowAllVoidLines")}
                        label="Allow All Void Lines"
                        name="AllowAllVoidLines"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowTablePlanning}
                        onToggle={(checked) => handleToggleChange(checked, "AllowTablePlanning")}
                        label="Allow Table Planning"
                        name="AllowTablePlanning"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.CanManageSchedule}
                        onToggle={(checked) => handleToggleChange(checked, "CanManageSchedule")}
                        label="Can Manage Schedule"
                        name="CanManageSchedule"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.CanAccessPOSwithoutClockin}
                        onToggle={(checked) => handleToggleChange(checked, "CanAccessPOSwithoutClockin")}
                        label="Can Access POS Without Clock-in"
                        name="CanAccessPOSwithoutClockin"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.CanAccessReports}
                        onToggle={(checked) => handleToggleChange(checked, "CanAccessReports")}
                        label="Can Access Reports"
                        name="CanAccessReports"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.CanBypassLogin}
                        onToggle={(checked) => handleToggleChange(checked, "CanBypassLogin")}
                        label="Can Bypass Login"
                        name="CanBypassLogin"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                </div>
              </PanelBody>
            </Panel>
          </div>

          {/* Till Discount Options Panel */}
          <div className="col-xl-12">
            <Panel className="m-3" data-sortable-id="form-stuff-1">
              <PanelHeader>
                <h4 className="panel-title">Till Discount Options</h4>
              </PanelHeader>
              <PanelBody>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowBasketDiscounts}
                        onToggle={(checked) => handleToggleChange(checked, "AllowBasketDiscounts")}
                        label="Allow Basket Discounts"
                        name="AllowBasketDiscounts"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  {formData.AllowBasketDiscounts && (
                    <>
                      <div className="col-md-4">
                        <FormInput
                          label="Order Discount Limit %"
                          placeHolder="Enter Discount Percentage"
                          type="number"
                          value={formData.BasketDiscountLimitPercentage || ""}
                          onchange={handleInputChange}
                          name="BasketDiscountLimitPercentage"
                          readOnly={isViewOnly}
                          disabled={isViewOnly}
                        />
                      </div>
                      <div className="col-md-4">
                        <FormInput
                          label="Maximum discount value"
                          placeHolder="Enter Maximum discount value"
                          type="number"
                          value={formData.BasketDiscountLimit || ""}
                          onchange={handleInputChange}
                          name="BasketDiscountLimit"
                          readOnly={isViewOnly}
                          disabled={isViewOnly}
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowRefunds}
                        onToggle={(checked) => handleToggleChange(checked, "AllowRefunds")}
                        label="Allow Refunds"
                        name="AllowRefunds"
                        themeColor={themeColor}
                        disabled={isViewOnly}
                      />
                    </div>
                  </div>
                  {formData.AllowRefunds && (
                    <div className="col-md-4">
                      <FormInput
                        label="Refund Limit"
                        placeHolder="Enter Refund Limit"
                        type="number"
                        value={formData.RefundLimit || ""}
                        onchange={handleInputChange}
                        name="RefundLimit"
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                      />
                    </div>
                  )}
                </div>
              </PanelBody>
            </Panel>
          </div>
        </div>
      </div>
</GridSectionLoader>
    </div>
  );
}

export default UpdateRole;